import { useTranslation } from 'react-i18next';
import type { AirlineBaggageRuleItem } from '@/api/generated/schemas';
import { AirlineCheckInType, AirlineTicketTypes } from '@/api/generated/schemas';
import { SegmentedField, TextField } from '@/shared/forms';
import { Alert, Panel } from '@/shared/ui';
import { AIRLINE_PURCHASE_STRATEGIES, type AirlinePurchaseStrategy } from '../airlineFormSchema';
import type { AirlineFormApi } from '../useAirlineForm';
import { AirlineBaggagePanel, type CarrierCurrency } from './AirlineBaggagePanel';
import { AirlineNameChangePanel } from './AirlineNameChangePanel';

const LABEL_W = 168;

interface AirlineExtraServicesTabProps {
  form: AirlineFormApi;
  /** Bez `airlineId` je dopravce v režimu zakládání — pravidla nemají na čem viset. */
  airlineId?: number;
  baggageRules: AirlineBaggageRuleItem[];
  /** **Uložená** měna dopravce (ne hodnota formuláře) — v ní se vyjadřují ceny pravidel. */
  carrierCurrency: CarrierCurrency | null;
}

/**
 * Záložka Extra services — sekce „Tickets & check-in" a „Name changes" (pole formuláře)
 * a „Baggage" (vlastní CRUD).
 * <p>
 * Typ letenek i typ check-inu jsou povinné explicitní volby (žádná se nepředvyplňuje), strategie
 * nákupu je vícehodnotový výběr. Okno online check-inu se zobrazí jen u `Online`; přepnutí na
 * letiště obě hodnoty rovnou vymaže, aby si uživatel nenesl neviditelná čísla dál (server je
 * normalizuje nezávisle na klientovi).
 * </p>
 */
export function AirlineExtraServicesTab({
  form,
  airlineId,
  baggageRules,
  carrierCurrency,
}: AirlineExtraServicesTabProps) {
  const { t } = useTranslation('inventory');

  const ticketTypeOptions = Object.values(AirlineTicketTypes).map((value) => ({
    value,
    label: t(`airlineTicketTypes.${value}`),
  }));

  const strategyOptions = AIRLINE_PURCHASE_STRATEGIES.map((value) => ({
    value,
    label: t(`airlinePurchaseStrategy.${value}`),
  }));

  const checkInOptions = Object.values(AirlineCheckInType).map((value) => ({
    value,
    label: t(`airlineCheckInType.${value}`),
  }));

  return (
    <div className="gap-phx-3 flex flex-col">
      <Panel variant="filled" icon="plane" title={t('airline.sections.tickets')}>
        <div className="gap-phx-3 flex flex-col">
          <form.Field name="ticketTypes">
            {(field) => (
              <SegmentedField<AirlineTicketTypes>
                field={field}
                layout="inline"
                labelWidth={LABEL_W}
                label={t('airline.ticketTypes.label')}
                required
                options={ticketTypeOptions}
                testId="airline-ticket-types"
              />
            )}
          </form.Field>

          <form.Field name="purchaseStrategies">
            {(field) => (
              <SegmentedField<AirlinePurchaseStrategy>
                field={field}
                multiple
                layout="inline"
                labelWidth={LABEL_W}
                label={t('airline.purchaseStrategies.label')}
                required
                options={strategyOptions}
                testId="airline-purchase-strategies"
              />
            )}
          </form.Field>

          <form.Field
            name="checkInType"
            listeners={{
              // Letištní odbavení okno nemá — hodnoty se mažou hned při přepnutí, ne až při uložení,
              // aby se uživateli nevrátila neviditelná čísla při návratu na Online.
              onChange: ({ value }) => {
                if (value !== AirlineCheckInType.Online) {
                  form.setFieldValue('checkInWindowFromHours', '');
                  form.setFieldValue('checkInWindowToHours', '');
                }
              },
            }}
          >
            {(field) => (
              <SegmentedField<AirlineCheckInType>
                field={field}
                layout="inline"
                labelWidth={LABEL_W}
                label={t('airline.checkInType.label')}
                required
                options={checkInOptions}
                testId="airline-check-in-type"
              />
            )}
          </form.Field>

          <form.Subscribe selector={(state) => state.values.checkInType}>
            {(checkInType) =>
              checkInType === AirlineCheckInType.Online ? (
                <div className="gap-phx-3 flex flex-col">
                  <form.Field name="checkInWindowFromHours">
                    {(field) => (
                      <TextField
                        field={field}
                        type="number"
                        min={0}
                        layout="inline"
                        labelWidth={LABEL_W}
                        label={t('airline.checkInWindow.fromLabel')}
                        required
                        helperText={t('airline.checkInWindow.hint')}
                        data-testid="airline-check-in-window-from"
                      />
                    )}
                  </form.Field>

                  <form.Field name="checkInWindowToHours">
                    {(field) => (
                      <TextField
                        field={field}
                        type="number"
                        min={0}
                        layout="inline"
                        labelWidth={LABEL_W}
                        label={t('airline.checkInWindow.toLabel')}
                        required
                        data-testid="airline-check-in-window-to"
                      />
                    )}
                  </form.Field>
                </div>
              ) : null
            }
          </form.Subscribe>

          <form.Subscribe selector={(state) => state.values.ticketTypes}>
            {(ticketTypes) =>
              ticketTypes === AirlineTicketTypes.Individual ? (
                <Alert severity="warning" data-testid="airline-no-group-booking">
                  {t('airline.ticketTypes.noGroupBookingWarning')}
                </Alert>
              ) : null
            }
          </form.Subscribe>
        </div>
      </Panel>

      {/* Ceny pravidel se vyjadřují v měně dopravce, takže dokud je změna měny neuložená, nesmí
          se pravidlo uložit — endpoint by ji stejně validoval proti persistované hodnotě. */}
      <form.Subscribe selector={(state) => state.values.currency?.id ?? null}>
        {(formCurrencyId) => (
          <AirlineBaggagePanel
            airlineId={airlineId}
            rules={baggageRules}
            carrierCurrency={carrierCurrency}
            currencyUnsaved={formCurrencyId !== (carrierCurrency?.id ?? null)}
          />
        )}
      </form.Subscribe>

      <AirlineNameChangePanel form={form} />
    </div>
  );
}
